import isEmpty from 'lodash/isEmpty';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';

export { toNumber };
const PACKAGING_FALLBACK_TEXT = '—';
const packagingCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
});

export const isPositiveNumber = (value) => {
    if (isNil(value) || value === '') return false;
    const parsed = toNumber(value);
    return isFinite(parsed) && parsed > 0;
};

/**
 * Computes total cost for a packaging PO item.
 * Formula: unit price * (units per case * qty cases).
 * @param {{ qty?: *, unitsPerCase?: *, unitPrice?: * }} item
 * @returns {number|null}
 */
export const computePackagingItemTotalCost = (item) => {
    if (
        item?.qty === '' ||
        item?.unitsPerCase === '' ||
        item?.unitPrice === '' ||
        isNil(item?.qty) ||
        isNil(item?.unitsPerCase) ||
        isNil(item?.unitPrice)
    ) {
        return null;
    }
    const qty = toNumber(item?.qty);
    const unitsPerCase = toNumber(item?.unitsPerCase);
    const unitPrice = toNumber(item?.unitPrice);
    if (!isFinite(qty) || !isFinite(unitsPerCase) || !isFinite(unitPrice)) return null;
    return qty * unitsPerCase * unitPrice;
};

export const formatPackagingCurrency = (value) => {
    if (isNil(value) || value === '') return PACKAGING_FALLBACK_TEXT;
    const numericValue = toNumber(value);
    if (!isFinite(numericValue)) return PACKAGING_FALLBACK_TEXT;
    return packagingCurrencyFormatter.format(numericValue);
};

export const isValidCreatePackagingItem = (item) => {
    if (isNil(item)) return false;
    return (
        isPositiveNumber(item.id) &&
        isPositiveNumber(item.qty) &&
        isPositiveNumber(item.unitPrice) &&
        isPositiveNumber(item.numPallets) &&
        isPositiveNumber(item.unitsPerCase)
    );
};

export const buildSubmitItems = (items = []) =>
    items.map((item) => ({
        id: item.id,
        qty: item.qty,
        unitPrice: item.unitPrice,
        vendorSku: item.vendorSku,
        numPallets: item.numPallets,
        unitsPerCase: item.unitsPerCase,
        unitsPerCaseUOM: item.unitsPerCaseUOM,
        notes: item.notes,
    }));

export const toIntOrZero = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const n = Number.parseInt(String(value), 10);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Validates primary (header) draft for packaging PO edit.
 * @param {Object} draft - Primary draft (vendorId, scheduledDeliveryDate, scheduledDeliveryTime, etc.)
 * @returns {{ errors: string[], fieldErrors: Object }}
 */
export const validatePackagingPrimaryDraft = (draft) => {
    const errors = [];
    const fieldErrors = {};

    if (!draft?.vendorId) {
        fieldErrors.vendorId = 'Vendor is required.';
        errors.push('Vendor is required.');
    }
    if (!draft?.scheduledDeliveryDate) {
        fieldErrors.scheduledDeliveryDate = 'Scheduled delivery date is required.';
        errors.push('Scheduled delivery date is required.');
    }
    if (!draft?.scheduledDeliveryTime) {
        fieldErrors.scheduledDeliveryTime = 'Scheduled delivery time is required.';
        errors.push('Scheduled delivery time is required.');
    }
    return { errors, fieldErrors };
};

/**
 * Builds the API payload for updating packaging PO primary fields.
 * Normalizes referenceNumber so that clearing the field sends '' to the API.
 * Mirrors POv2 payload construction pattern (buildPurchaseOrderPrimaryUpdatePayload).
 *
 * @param {Object} currentPrimary - Current primary draft (scheduledDeliveryDate, scheduledDeliveryTime, referenceNumber, vendorId)
 * @returns {Object} API payload: { scheduledDeliveryDate, deliverTime, referenceNumber, vendorID }
 */
export function buildPackagingPrimaryUpdatePayload(currentPrimary) {
    if (!currentPrimary) {
        return {};
    }
    return {
        scheduledDeliveryDate: currentPrimary.scheduledDeliveryDate ?? '',
        deliverTime: currentPrimary.scheduledDeliveryTime ?? '',
        referenceNumber: currentPrimary.referenceNumber != null ? String(currentPrimary.referenceNumber) : '',
        vendorID: currentPrimary.vendorId ?? null,
        shipToCrossDockID: currentPrimary.crossDockId ? Number(currentPrimary.crossDockId) : null,
    };
}

const PACKAGING_MAP_ITEM_FIELDS = ['qty', 'unitPrice', 'numPallets', 'unitsPerCase', 'vendorSku', 'notes'];

/**
 * Normalizes the packaging PO map item id used for update/remove APIs.
 * @param {Object} item - Row from entity items array
 * @returns {string|null}
 */
export function getPackagingPoMapId(item) {
    const raw = item?.poMapID ?? item?.poMapId ?? item?.id ?? null;
    return raw != null ? String(raw) : null;
}

function normalizeMapItemValueForCompare(value) {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const s = String(value).trim();
    const num = Number(s);
    return Number.isFinite(num) ? num : s;
}

/**
 * Builds normalized packaging map item values from a draft (for diff comparison).
 * @param {Object} draft - Object with qty, unitPrice, numPallets, unitsPerCase, vendorSku, notes
 * @returns {Object}
 */
function buildPackagingMapItemUpdates(draft) {
    const updates = {};
    PACKAGING_MAP_ITEM_FIELDS.forEach((field) => {
        const v = draft?.[field];
        if (field === 'qty' || field === 'numPallets' || field === 'unitsPerCase') {
            const n = toIntOrZero(v);
            updates[field] = n;
        } else if (field === 'unitPrice') {
            const num = isNil(v) || v === '' ? null : toNumber(v);
            updates[field] = isFinite(num) ? num : (v ?? '');
        } else {
            updates[field] = v != null ? String(v) : '';
        }
    });
    return updates;
}

/**
 * Computes a diff of packaging item edits: only fields that changed (UI draft vs original item).
 * @param {{ item: Object, uiDraft: Object }} params
 * @returns {Object} Payload suitable for updatePurchaseOrderMap (only changed fields)
 */
export function buildPackagingMapItemDiff({ item, uiDraft }) {
    const updates = buildPackagingMapItemUpdates(uiDraft);
    const originalForCompare = buildPackagingMapItemUpdates({
        qty: item?.qty ?? '',
        unitPrice: item?.unitPrice ?? '',
        numPallets: item?.numPallets ?? '',
        unitsPerCase: item?.unitsPerCase ?? '',
        vendorSku: item?.vendorSku ?? '',
        notes: item?.notes ?? '',
    });

    const diff = {};
    PACKAGING_MAP_ITEM_FIELDS.forEach((key) => {
        const a = normalizeMapItemValueForCompare(updates[key]);
        const b = normalizeMapItemValueForCompare(originalForCompare[key]);
        if (String(a) !== String(b)) {
            diff[key] = updates[key];
        }
    });
    return diff;
}

const ITEM_VALIDATION_MESSAGE =
    'Complete required fields for each item: Quantity (cases), Unit Price, Pallets per SKU, and Units per Case.';

/**
 * Derived validation for packaging PO entity edit (primary + added items + persisted items).
 * Validates that all added items and all non-removed existing items have required fields (qty, unitPrice, numPallets, unitsPerCase).
 * @param {Object} params
 * @param {Object} params.primaryDraft
 * @param {Array} params.addedItems
 * @param {Array} [params.entityItems] - Existing items from the PO (validated when edited)
 * @param {Set<string>} params.removedItemIds
 * @param {React.MutableRefObject<{ inputs: Object }>} params.itemEditStateRef
 * @param {function(React.MutableRefObject, string, string):*} params.getItemEditInputValue
 * @returns {{ isValid: boolean, primaryErrors: string[], primaryFieldErrors: Object, itemErrors: string[] }}
 */
export function validatePackagingPOEdit({
    addedItems = [],
    entityItems = [],
    getItemEditInputValue,
    itemEditStateRef,
    primaryDraft,
    removedItemIds = new Set(),
}) {
    const { errors: primaryErrors, fieldErrors: primaryFieldErrors } = validatePackagingPrimaryDraft(
        primaryDraft ?? {},
    );

    const itemErrors = [];
    const newItemsToAdd = (addedItems ?? []).filter((item) => !removedItemIds.has(String(item?.id)));

    const normalizedAddedForValidation =
        newItemsToAdd.length > 0
            ? newItemsToAdd.map((item) => {
                  const rowId = String(item.id);
                  const qty = getItemEditInputValue(itemEditStateRef, rowId, 'qty') ?? item.qty;
                  const unitPrice = getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice') ?? item.unitPrice;
                  const vendorSku = getItemEditInputValue(itemEditStateRef, rowId, 'vendorSku') ?? item.vendorSku;
                  const numPalletsRaw = getItemEditInputValue(itemEditStateRef, rowId, 'numPallets') ?? item.numPallets;
                  const numPallets = toIntOrZero(numPalletsRaw);
                  const unitsPerCase =
                      getItemEditInputValue(itemEditStateRef, rowId, 'unitsPerCase') ?? item.unitsPerCase;
                  return {
                      id: item.packagingModelId,
                      vendorSku: vendorSku ?? '',
                      qty,
                      unitPrice,
                      numPallets: numPallets === 0 ? '' : numPallets,
                      unitsPerCase,
                  };
              })
            : [];
    const hasInvalidAddedItems = normalizedAddedForValidation.some((row) => !isValidCreatePackagingItem(row));

    const persistedItems = (entityItems ?? []).filter((item) => {
        const id = getPackagingPoMapId(item);
        if (!id || String(id).startsWith('new-')) return false;
        if (removedItemIds.has(id)) return false;
        return true;
    });
    const normalizedPersistedForValidation =
        persistedItems.length > 0
            ? persistedItems.map((item) => {
                  const rowId = getPackagingPoMapId(item);
                  const qty = getItemEditInputValue(itemEditStateRef, rowId, 'qty') ?? item.qty;
                  const unitPrice = getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice') ?? item.unitPrice;
                  const vendorSku = getItemEditInputValue(itemEditStateRef, rowId, 'vendorSku') ?? item.vendorSku;
                  const numPalletsRaw = getItemEditInputValue(itemEditStateRef, rowId, 'numPallets') ?? item.numPallets;
                  const numPallets = toIntOrZero(numPalletsRaw);
                  const unitsPerCase =
                      getItemEditInputValue(itemEditStateRef, rowId, 'unitsPerCase') ?? item.unitsPerCase;
                  return {
                      id: 1,
                      vendorSku: vendorSku ?? '',
                      qty,
                      unitPrice,
                      numPallets: numPallets === 0 ? '' : numPallets,
                      unitsPerCase,
                  };
              })
            : [];
    const hasInvalidPersistedItems = normalizedPersistedForValidation.some((row) => !isValidCreatePackagingItem(row));

    if (hasInvalidAddedItems || hasInvalidPersistedItems) {
        itemErrors.push(ITEM_VALIDATION_MESSAGE);
    }

    const isValid = primaryErrors.length === 0 && itemErrors.length === 0;
    return {
        isValid,
        primaryErrors,
        primaryFieldErrors,
        itemErrors,
    };
}

export const extractFacilityIds = (facilities) => {
    if (!Array.isArray(facilities)) return [];
    return facilities
        .map((facility) => {
            if (isNil(facility) || facility === '') return null;
            if (typeof facility === 'object' && !isNil(facility.id)) return toNumber(facility.id);
            const parsed = toNumber(facility);
            return isFinite(parsed) ? parsed : null;
        })
        .filter((value) => !isNil(value));
};

export const validateCreatePackagingPO = ({ draft = {}, items } = {}) => {
    const primaryErrors = [];
    const primaryFieldErrors = {};
    const itemErrors = [];

    const addPrimaryError = (field, message) => {
        primaryErrors.push(message);
        primaryFieldErrors[field] = message;
    };

    if (!draft.vendorId) {
        addPrimaryError('vendorId', 'Vendor is required');
    }
    if (!draft.scheduledDeliveryDate) {
        addPrimaryError('scheduledDeliveryDate', 'Scheduled delivery date is required');
    }
    if (extractFacilityIds(draft.facilities).length === 0) {
        addPrimaryError('facilityId', 'Facility is required');
    }

    const normalizedItems = Array.isArray(items) ? items : [];
    if (isEmpty(normalizedItems)) {
        itemErrors.push('Add at least one item.');
    }

    const submitItems = buildSubmitItems(normalizedItems);
    const hasItemFieldErrors = submitItems.some((item) => !isValidCreatePackagingItem(item));

    if (hasItemFieldErrors) {
        itemErrors.push(ITEM_VALIDATION_MESSAGE);
    }

    return {
        isValid: primaryErrors.length === 0 && itemErrors.length === 0,
        primaryErrors,
        primaryFieldErrors,
        itemErrors,
        submitItems,
    };
};
